import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchJson } from '../apiBase';
import { Skeleton } from './components/ui';

const bgStyle = {
  minHeight: '100vh',
  background: 'radial-gradient(circle at 15% 20%, rgba(45,55,72,0.65), rgba(15,23,42,0.9) 40%), radial-gradient(circle at 80% 10%, rgba(30,64,175,0.45), rgba(12,18,38,0.95) 45%)',
  padding: '20px 16px 60px',
  color: '#e5e7eb',
};

function useToken() {
  return useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('token') || '';
    } catch {
      return '';
    }
  }, []);
}

function ensureFont() {
  const id = 'kk-values-font';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const chipBase = {
  border: '1px solid rgba(255,255,255,0.12)',
  padding: '10px 14px',
  borderRadius: 14,
  fontWeight: 600,
  cursor: 'pointer',
  minWidth: 82,
  textAlign: 'center',
  transition: 'all 0.16s ease',
};

function AnswerChip({ label, active, tone, onClick }) {
  const palette = tone === 'yes'
    ? { bg: 'linear-gradient(120deg, #22c55e, #10b981)', color: '#022c16' }
    : { bg: 'linear-gradient(120deg, #f97316, #fb7185)', color: '#2f0a0a' };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...chipBase,
        background: active ? palette.bg : 'transparent',
        color: active ? palette.color : '#cbd5e1',
        boxShadow: active ? '0 12px 30px rgba(0,0,0,0.25)' : 'none',
      }}
    >
      {label}
    </button>
  );
}

function TextAreaField({ label, name, value, onChange, placeholder }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ color: '#cbd5e1', fontSize: 14, fontWeight: 600 }}>{label}</div>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          background: '#0b1223',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
          padding: 12,
          color: '#e5e7eb',
          fontFamily: 'inherit',
          minHeight: 100,
          resize: 'vertical',
        }}
      />
    </div>
  );
}

export default function ValuesEvaluationForm() {
  const { themeId, formId } = useParams();
  const token = useToken();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [general, setGeneral] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [studentMeta, setStudentMeta] = useState({ name: '', class_name: '' });
  const [parentName, setParentName] = useState('');

  useEffect(() => {
    ensureFont();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Bağlantı eksik veya hatalı.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await fetchJson(
          `/parent/values/${encodeURIComponent(themeId)}/${encodeURIComponent(formId)}?token=${encodeURIComponent(token)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) throw new Error(`Sunucu hatası (${res.status})`);
        const data = await res.json();
        setForm(data.form);
        setStudentMeta(data.student || {});
        setParentName(data.parent_display || '');
        const initial = {};
        (data.form?.sections || []).forEach((sec) => {
          (sec.questions || []).forEach((q) => {
            initial[q.id] = '';
          });
        });
        const prefills = data.answers || {};
        setAnswers({ ...initial, ...prefills });
        setNotes(data.notes || '');
        setStrengths(data.strengths || '');
        setImprovements(data.improvements || '');
        setGeneral(data.general_comment || '');
      } catch (err) {
        setError('Form yüklenemedi. Lütfen bağlantıyı yeniden açmayı deneyin.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [themeId, formId, token]);

  const selectAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const allAnswered = useMemo(() => {
    if (!form) return false;
    return form.sections.every((sec) =>
      (sec.questions || []).every((q) => (answers[q.id] || '').length > 0)
    );
  }, [form, answers]);

  const handleSubmit = async () => {
    if (!form) return;
    if (!allAnswered) {
      setStatus('Lütfen tüm soruları cevaplayın.');
      return;
    }
    setSubmitting(true);
    setStatus('');
    try {
      const payload = {
        token,
        answers,
        notes,
        strengths,
        improvements,
        general_comment: general,
      };
      const res = await fetchJson(
        `/parent/values/${encodeURIComponent(themeId)}/${encodeURIComponent(formId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error();
      setStatus('Gönderildi. Teşekkür ederiz!');
    } catch (_) {
      setStatus('Kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestions = () => (
    (form?.sections || []).map((section) => (
      <div
        key={section.id}
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: 16,
          boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 22 }}>{section.emoji || '⭐️'}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#f8fafc' }}>{section.title}</div>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {(section.questions || []).map((q) => (
            <div
              key={q.id}
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14,
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ color: '#e2e8f0', fontWeight: 600, lineHeight: 1.4 }}>{q.text}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <AnswerChip
                  label="Evet"
                  tone="yes"
                  active={answers[q.id] === 'yes'}
                  onClick={() => selectAnswer(q.id, 'yes')}
                />
                <AnswerChip
                  label="Hayır"
                  tone="no"
                  active={answers[q.id] === 'no'}
                  onClick={() => selectAnswer(q.id, 'no')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))
  );

  if (loading) {
    return (
      <div style={{ ...bgStyle, fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}>
        <Skeleton width="100%" height={52} radius={12} />
        <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
          {[0, 1].map((i) => <Skeleton key={i} width="100%" height={120} radius={14} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...bgStyle, fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif", display: 'grid', placeItems: 'center' }}>
        <div style={{ background: '#0b1223', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 24, maxWidth: 520 }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#f1f5f9' }}>Forma erişilemiyor</div>
          <div style={{ color: '#cbd5e1', lineHeight: 1.5 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...bgStyle, fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}>
      <div
        style={{
          display: 'grid',
          gap: 12,
          padding: 18,
          background: 'linear-gradient(145deg, rgba(30,41,59,0.9), rgba(15,23,42,0.92))',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 18,
          boxShadow: '0 30px 80px rgba(0,0,0,0.32)',
          color: '#e2e8f0',
        }}
      >
        <div style={{ textTransform: 'uppercase', letterSpacing: 0.6, color: '#9ca3af', fontSize: 13, fontWeight: 700 }}>
          Değer çalışması
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: '#f8fafc' }}>{form?.title}</div>
        <div style={{ color: '#cbd5e1', lineHeight: 1.5 }}>{form?.description}</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.4)', color: '#34d399', padding: '8px 12px', borderRadius: 999 }}>
            {parentName || 'Veli'}
          </div>
          <div style={{ background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.45)', color: '#bfdbfe', padding: '8px 12px', borderRadius: 999 }}>
            {studentMeta.name || 'Öğrenci'} {studentMeta.class_name ? `• ${studentMeta.class_name}` : ''}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18, display: 'grid', gap: 14 }}>
        {renderQuestions()}

        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 18,
            padding: 16,
            boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
            display: 'grid',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>Notlar</div>
          <TextAreaField label={form?.notes_label} name="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Özel durumlar, olumlu davranışlar veya geliştirilmesi gereken noktalar..." />
          <TextAreaField label={form?.strengths_label} name="strengths" value={strengths} onChange={(e) => setStrengths(e.target.value)} placeholder="Parladığı alanlar..." />
          <TextAreaField label={form?.improvements_label} name="improvements" value={improvements} onChange={(e) => setImprovements(e.target.value)} placeholder="Desteklemek istediğiniz alanlar..." />
          <TextAreaField label={form?.general_label} name="general" value={general} onChange={(e) => setGeneral(e.target.value)} placeholder="Genel yorumunuz..." />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: 'linear-gradient(120deg, #38bdf8, #22d3ee)',
              color: '#0b1223',
              fontWeight: 800,
              padding: '14px 20px',
              border: 'none',
              borderRadius: 14,
              boxShadow: '0 18px 40px rgba(56,189,248,0.35)',
              cursor: 'pointer',
              letterSpacing: 0.2,
              opacity: submitting ? 0.75 : 1,
            }}
          >
            {submitting ? 'Gönderiliyor…' : 'Formu Gönder'}
          </button>
          {status && (
            <div style={{ color: status.includes('Teşekkür') ? '#34d399' : '#fbbf24', fontWeight: 600 }}>
              {status}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
